import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  PersonAdd,
  People,
  Visibility,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetUsersQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteUserMutation } from '../store/api/usersApi'
import { hasPermission } from '../utils/permissions'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx } from '../theme/sectionSurfaces'

const ROLES = [
  { value: 'secretary', label: 'Ban Thư ký' },
  { value: 'judge', label: 'Giám khảo' },
  { value: 'student', label: 'Sinh viên' },
  { value: 'coach', label: 'Huấn luyện viên' },
  { value: 'support', label: 'Hỗ trợ kỹ thuật' },
]

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  secretary: 'error',
  judge: 'primary',
  student: 'success',
  coach: 'info',
  support: 'warning',
}

export default function UsersPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: users = [], isLoading, error, refetch } = useGetUsersQuery()
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation()
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [editingUser, setEditingUser] = useState<any>(null)
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    role: 'student',
    password: '',
  })
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Check permission
  const canManageUsers = currentUser && hasPermission(currentUser.role, 'MANAGE_USERS')

  useEffect(() => {
    if (!canManageUsers) {
      setErrorMessage('Bạn không có quyền truy cập trang này. Chỉ Secretary và Support mới có thể quản lý người dùng.')
    }
  }, [canManageUsers])

  // View user dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingUser, setViewingUser] = useState<any>(null)

  // Create user function - matches moduleFunctions.ts
  const handleCreate = () => {
    setEditingUser(null)
    setFormData({
      username: '',
      email: '',
      full_name: '',
      role: 'student',
      password: '',
    })
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  // Update user function - matches moduleFunctions.ts
  const handleUpdate = (user: any) => {
    setEditingUser(user)
    setFormData({
      username: user.username || '',
      email: user.email || '',
      full_name: user.full_name || '',
      role: user.role || 'student',
      password: '', // Don't prefill password
    })
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  // View user function - matches moduleFunctions.ts
  const handleView = (user: any) => {
    setViewingUser(user)
    setViewDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingUser(null)
    setFormData({
      username: '',
      email: '',
      full_name: '',
      role: 'student',
      password: '',
    })
    // Chỉ clear error message trong dialog, không clear success message
    // Success message sẽ được hiển thị trên trang chính
    setErrorMessage(null)
  }

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingUser(null)
  }

  // Submit handler - internal function, calls handleCreate or handleUpdate logic
  const handleSubmit = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      if (editingUser) {
        // Update user
        const updateData: any = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
        }
        if (formData.password) {
          updateData.password = formData.password
        }
        await updateUser({ id: editingUser.id, ...updateData } as any).unwrap()
        // Đóng dialog trước, sau đó set success message để hiển thị trên trang chính
        handleCloseDialog()
        setSuccessMessage('Cập nhật người dùng thành công!')
      } else {
        // Create user
        if (!formData.password) {
          setErrorMessage('Mật khẩu là bắt buộc khi tạo người dùng mới')
          return
        }
        await createUser(formData).unwrap()
        // Đóng dialog trước, sau đó set success message để hiển thị trên trang chính
        handleCloseDialog()
        setSuccessMessage('Tạo người dùng thành công!')
      }
      // Refresh danh sách users
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa người dùng này?')) {
      return
    }

    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      await deleteUser(userId).unwrap()
      setSuccessMessage('Xóa người dùng thành công!')
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi xóa người dùng')
    }
  }

  if (!canManageUsers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Bạn không có quyền truy cập trang này. Chỉ Secretary và Support mới có thể quản lý người dùng.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Quản lý Người dùng"
        subtitle="Tạo và cập nhật tài khoản theo vai trò (Ban Thư ký, Giám khảo, Sinh viên, …)."
        icon={<People />}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAdd />}
            onClick={handleCreate}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
            }}
          >
            Tạo người dùng mới
          </Button>
        }
      />

      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }} 
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Username</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Họ tên</strong></TableCell>
                <TableCell><strong>Vai trò</strong></TableCell>
                <TableCell><strong>Trạng thái</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Chưa có người dùng nào</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user: any) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={ROLES.find(r => r.value === user.role)?.label || user.role}
                        color={ROLE_COLORS[user.role] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Hoạt động' : 'Vô hiệu hóa'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleView(user)}
                        color="info"
                        title="Xem chi tiết"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdate(user)}
                        color="primary"
                        title="Chỉnh sửa"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user.id)}
                        color="error"
                        disabled={user.id === currentUser?.id} // Can't delete yourself
                        title="Xóa"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Chỉnh sửa người dùng' : 'Tạo người dùng mới'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={!!editingUser} // Can't change username when editing
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Họ tên"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Vai trò"
              select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              fullWidth
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={editingUser ? 'Mật khẩu mới (để trống nếu không đổi)' : 'Mật khẩu'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingUser}
              fullWidth
              helperText={editingUser ? 'Chỉ điền nếu muốn đổi mật khẩu' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Hủy</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
            }}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : (editingUser ? 'Cập nhật' : 'Tạo')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Chi tiết người dùng</DialogTitle>
        <DialogContent>
          {viewingUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Username"
                value={viewingUser.username}
                disabled
                fullWidth
              />
              <TextField
                label="Email"
                value={viewingUser.email}
                disabled
                fullWidth
              />
              <TextField
                label="Họ tên"
                value={viewingUser.full_name || '-'}
                disabled
                fullWidth
              />
              <TextField
                label="Vai trò"
                value={ROLES.find(r => r.value === viewingUser.role)?.label || viewingUser.role}
                disabled
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={viewingUser.is_active ? 'Hoạt động' : 'Vô hiệu hóa'}
                  color={viewingUser.is_active ? 'success' : 'default'}
                  size="small"
                />
                <Chip
                  label={ROLES.find(r => r.value === viewingUser.role)?.label || viewingUser.role}
                  color={ROLE_COLORS[viewingUser.role] || 'default'}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
          {viewingUser && (
            <Button
              onClick={() => {
                handleCloseViewDialog()
                handleUpdate(viewingUser)
              }}
              variant="contained"
              startIcon={<Edit />}
            >
              Chỉnh sửa
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}
